import { DataSourceService }  from "../../../../services/http/datasource/DataSourceService"
import { DateService } from "../../../../services/types/DateService";
import { PumpPerformanceCurvesService } from "../../../../services/http/pumpperformancecurves/PumpPerformanceCurvesService";
import { PumpPerformanceCurvesParameters} from "../../../../services/http/pumpperformancecurves/models/PumpPerformanceCurvesParameters";
import { ReactElement, RefObject, useRef } from "react";
import { useDependency } from "../../../../contexts/DependencyContext";
import { useTimeMachine } from "../../../../contexts/TimeMachineContext";
import { useRecurrentAsyncCall } from "../../../../hooks/UseAsyncCall";
import LineChartUnit, { ILineChartSeries, ChartPointsPerPixelRatio } from "../../LineChartUnit";
import { DataSource } from "../../../../services/http/datasource/models/DataSource";
import Spinner from "../../Spinner";
import { TooltipItem } from "chart.js";
import NoData from "../../NoData";
import styles from "./PumpPerformanceCurves.module.scss"
import IndicatorNumber from "../../IndicatorNumber";

interface IValuesCalculated
{
    valuesForHead: number[];
    valuesForShaftPower: number[];
    valuesForPumpEfficiency: number[];
    xvalues: number[];
    unit: number;
}

interface IValuesSeries
{
    frequencyDataSource: DataSource;
    flowRateDataSource: DataSource;
    flowRate: number;
    valuesForHead: number[];
    valuesForShaftPower: number[];
    valuesForPumpEfficiency: number[];
    xvalues: number[];
    bep: number;
    operatingPoint: number;
    minXBar: number;
    maxXBar: number;
    unit: number;
    xFlowRate: number;
}

interface IPumpPerformanceCurvesProps
{
    A: number;
    B: number;
    C: number;
    D: number;
    E: number;
    F: number;
    G: number;
    H: number;
    I: number;
    frequencyDataSourceId: number;
    flowRateDataSourceId: number;
    frequencyCorrection: number;
    baseFrequency: number;
    date: Date;
    uiConfiguration: string;
    baseBepFlowRate: number;
}


async function loadData(dataSourceService: DataSourceService, pumpPerformanceCurvesService: PumpPerformanceCurvesService, dateService: DateService,
                        date: Date, chartContainerRef: RefObject<HTMLDivElement>, uiConfiguration: string): Promise<IValuesSeries>
{
    const parameters = new PumpPerformanceCurvesParameters();
    const maxPoints = chartContainerRef.current ? Math.round(chartContainerRef.current.offsetWidth * ChartPointsPerPixelRatio) : 1;

    const uiConfig = JSON.parse(uiConfiguration)
    const frequencyDataSource = await dataSourceService.getById(uiConfig.frequencyDataSourceId);

    const flowRateDataSource = await dataSourceService.getById(uiConfig.flowRateDataSourceId);

    parameters.A = uiConfig.a;
    parameters.B = uiConfig.b;
    parameters.baseFrequency = uiConfig.baseFrequency;
    parameters.C = uiConfig.c;
    parameters.flowRateDataSourceId = uiConfig.flowRateDataSourceId;
    parameters.frequencyCorrection = uiConfig.frequencyCorrection;
    parameters.frequencyDataSourceId = uiConfig.frequencyDataSourceId;
    parameters.toDate = date ?? new Date();
    parameters.fromDate = dateService.addMinutes(parameters.toDate, -20);
    parameters.G = uiConfig.g;
    parameters.H = uiConfig.h;
    parameters.I = uiConfig.i;
    parameters.baseBepFlowRate = uiConfig.baseBepFlowRate;
    const valuesResult = await pumpPerformanceCurvesService.getCalculatedCurvesAsync(parameters);
    const end = valuesResult.headCurveRoot > valuesResult.pumpEfficiencyCurveRoot ? valuesResult.pumpEfficiencyCurveRoot : valuesResult.headCurveRoot;
    const series = await calculateSeries(maxPoints, end, valuesResult.alpha, uiConfig.a, uiConfig.b, uiConfig.c, uiConfig.d, uiConfig.e, uiConfig.f, uiConfig.g, uiConfig.h,
        uiConfig.i, valuesResult.minPOR, valuesResult.maxPOR);

    return {frequencyDataSource, flowRateDataSource, flowRate: valuesResult.flowRateMeasure,
            valuesForHead: series.valuesForHead, valuesForShaftPower: series.valuesForShaftPower,valuesForPumpEfficiency: series.valuesForPumpEfficiency, xvalues: series.xvalues,
            bep: valuesResult.bep, operatingPoint: valuesResult.operatingPoint, minXBar: valuesResult.minPOR, maxXBar: valuesResult.maxPOR, unit: series.unit, xFlowRate: valuesResult.flowRateMeasure }
}

const PumpPerformanceCurves = (props: IPumpPerformanceCurvesProps): ReactElement =>
{
    const dataSourceService = useDependency(DataSourceService);
    const pumpPerformanceCurvesService = useDependency(PumpPerformanceCurvesService);
    const dateService = useDependency(DateService);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const timeMachineDate = useTimeMachine();
    const { loading, content } = useRecurrentAsyncCall(parseInt(process.env.REACT_APP_CHART_REFRESH_RATE ?? "300000"), loadData, dataSourceService, pumpPerformanceCurvesService, dateService, timeMachineDate, chartContainerRef, props.uiConfiguration)
    let prevMaxY = 0;
    let maxY = 0;
    if(content?.valuesForHead)
    prevMaxY = Math.max(...content?.valuesForHead)
    let i = parseInt(prevMaxY.toFixed(0));
    if(i%2000 === 0)
    {
        maxY = i;
    }
    else
    {
        for( i ; i%2000 !== 0; i++)
        {
            maxY = i + 1;
        }
    }
    const getTooltipLabel = (tooltipItem: TooltipItem<"line">): string =>
    {
        const fixedRawValue = Number(tooltipItem.raw)?.toFixed(5);
        const floatValue = parseFloat(fixedRawValue);
        return floatValue.toString();
    };

    const getTooltipTitle = (tooltipItems: TooltipItem<"line">[]): string =>
    {
        if (!tooltipItems || tooltipItems.length === 0) return '';

        const index = Number(tooltipItems[0].dataIndex);

        if (index === null || isNaN(index))
            return '';

        return '';
    }

    const getChartSeries = (): ILineChartSeries[] =>
    {
        const valuesForHead = {
            yAxisID: 'y',
            title: "Head",
            values: content?.valuesForHead.map(x => x !== null ? x : null),
            labels: content?.xvalues.map(x => x.toFixed(0).toString())
        } as ILineChartSeries;

        const valuesForPumpEfficiency = {
            title: "Efficiency",
            yAxisID: 'y2',
            values: content?.valuesForPumpEfficiency.map(x => x !== null ? x : null),
            labels: content?.xvalues.map(x => x.toFixed(0).toString())
        } as ILineChartSeries;

        const valuesForShaftPower = {
            yAxisID: 'y1',
            title: "Power",
            values: content?.valuesForShaftPower.map(x => x !== null ? x : null),
            labels: content?.xvalues.map(x => x.toFixed(0).toString())
        } as ILineChartSeries;

        return [valuesForHead, valuesForShaftPower, valuesForPumpEfficiency];
    }

    const chartSeries = getChartSeries();
    const hasValidMeasures = chartSeries && chartSeries?.length > 0 && chartSeries[0].values?.length > 0;
    return (
        <div>
            {
                content &&
                content.flowRateDataSource &&
                content.frequencyDataSource &&
                content.xvalues &&
                <div className={styles.indicators}>
                    <IndicatorNumber label="BEP" unitOfMeasure="BPD" value={content.bep} decimalPlaces={2} />
                    <IndicatorNumber label="Operating Point" unitOfMeasure="%" value={content.operatingPoint} decimalPlaces={2} />
                </div>
            }
                    <div ref={chartContainerRef}>
                        {loading
                            ? <Spinner caption="Loading data..." />
                            : hasValidMeasures
                            ?
                            <div>
                                <LineChartUnit
                                    series={chartSeries}
                                    xLabel= "Flow Rate, BPD"
                                    yLabel= "Head, ft"
                                    y1Label = "Power, hp"
                                    y2Label= "Efficiency, %"
                                    getTooltipLabel= {getTooltipLabel}
                                    getTooltipTitle= {getTooltipTitle}
                                    timeUnit="minute"
                                    enableZoom={false}
                                    displayLegend
                                    size="small"
                                    minXBar = {content?.minXBar}
                                    maxXBar = {content?.maxXBar}
                                    unit = {content?.unit}
                                    maxY = {maxY}
                                    xFlowRate = {content?.xFlowRate}
                                />

                            </div>
                            : <NoData badgeText="No measures" message={`No measures received at ${dateService.format(timeMachineDate ?? new Date(), 'MM/dd/yyyy HH:mm:ss')}`} />

                        }
                        {
                        content &&
                        content.flowRateDataSource &&
                        content.frequencyDataSource &&
                        content.xvalues &&
                        <section className={styles.chartNotes}>
                            <div className={styles.item}>
                                <div className={`${styles.graphic} ${styles.flowRate}`} />
                                <p>Flow Rate</p>
                            </div>
                            <div className={styles.item}>
                                <div className={`${styles.graphic} ${styles.recommended}`} />
                                <p>Recommended Operating Range</p>
                            </div>
                        </section>
                        }
                    </div>
        </div>);
}

async function calculateSeries(maxPoints: number, end: number, alpha: number, A: number, B: number, C: number, D: number, E: number, F: number, G: number, H: number, I: number, minXBar: number, maxXBar: number): Promise<IValuesCalculated>
{
    let finish = 0;
    let finishPoint = 0;
    let i = parseInt(end.toFixed(0));
    if (i %500 === 0)
    {
        finish = i;
    }
    else
    {
        for(i ; i %500 !== 0; i++)
        {
            finish = i + 1;
        }
    }
    if(maxPoints %5 === 0)
    {
        finishPoint = maxPoints;
    }
    else
    {
        for(var j = maxPoints; j %5 !== 0; j--)
        {
            finishPoint = j - 1;
        }
    }
    const unit = finish / finishPoint;
    const valuesForHead: number[] = [];
    const valuesForShaftPower: number[] = [];
    const valuesForPumpEfficiency: number[] = [];
    const xvalues : number[] = [];
    for(var flowRate = 0; flowRate <= finish - 1; flowRate += unit)
    {
        if (flowRate <= end)
        {
            valuesForHead.push(alpha**2 * A + alpha * B * flowRate + C * flowRate**2);
            valuesForShaftPower.push(alpha**3 * D + alpha**2 * E * flowRate + alpha * F * flowRate**2)
            valuesForPumpEfficiency.push(G + H * flowRate / alpha + I * flowRate**2/alpha**2);
        }
        if((flowRate + unit) > finish)
            xvalues.push(finish);
        else
            xvalues.push(flowRate);
    }

    return {valuesForHead, valuesForShaftPower, valuesForPumpEfficiency, xvalues, unit};
}

export default PumpPerformanceCurves;